import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, FileText, FolderOpen, Shield,
  TrendingUp, Activity, DollarSign, Database,
  Menu, X, Bell, Search, Settings, LogOut,
  ChevronRight, Plus, MoreVertical, UserCheck, Key, ArrowLeft, User, ChevronDown,
  CreditCard, Package, Tag, Files, Send, AlertCircle, Mail, Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getNavigationItems, getUserRole as getRoleFromUser } from '../utils/roleRedirect';
import userService from '../services/userService';
import notificationApiService from '../services/notificationApiService';
import fileService from '../services/fileService';
import toast from 'react-hot-toast';
import moment from 'moment';
// import SecurityWrapper from './SecurityWrapper'; // Security features disabled
import useEchoNotifications from '../hooks/useEchoNotifications';

const Layout = ({ children }) => {
  const { user, logout, isImpersonating, originalUser, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is a dataroom file manager route
  const isDataroomRoute = location.pathname.includes('/datarooms/') && location.pathname.includes('/files');

  const [sidebarOpen, setSidebarOpen] = useState(!isDataroomRoute && window.innerWidth >= 1024);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifications, setNotifications] = useState([]);
  // Initialize unread count from localStorage for immediate display
  const [unreadCount, setUnreadCount] = useState(() => {
    const cached = localStorage.getItem('unreadNotificationCount');
    return cached ? parseInt(cached, 10) : 0;
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [searchType, setSearchType] = useState('filename'); // For file manager search dropdown
  const [searchQuery, setSearchQuery] = useState(''); // Global search query
  const [fileTypes, setFileTypes] = useState([]); // Selected file types for filtering
  const [showFileTypeDropdown, setShowFileTypeDropdown] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const fileTypeDropdownRef = useRef(null);
  const searchDropdownRef = useRef(null);

  // Store setSidebarOpen in a ref so child components can access it
  React.useEffect(() => {
    window.__setSidebarOpen = setSidebarOpen;
    return () => {
      delete window.__setSidebarOpen;
    };
  }, []);

  // Expose search query, search type, and file types globally for FileManager to access
  React.useEffect(() => {
    window.__searchQuery = searchQuery;
    window.__setSearchQuery = setSearchQuery;
    window.__searchType = searchType;
    window.__fileTypes = fileTypes;
    return () => {
      delete window.__searchQuery;
      delete window.__setSearchQuery;
      delete window.__searchType;
      delete window.__fileTypes;
    };
  }, [searchQuery, searchType, fileTypes]);
  
  // Use Echo notifications hook for real-time browser notifications
  // Subscribe to current user's channel and optionally additional test channels
  const additionalChannels = localStorage.getItem('test_notification_channels') 
    ? JSON.parse(localStorage.getItem('test_notification_channels')) 
    : [];
  
  const { 
    notifications: echoNotifications, 
    isConnected: echoConnected 
  } = useEchoNotifications(user?.id, additionalChannels);
  
  // Log Echo connection status and user info
  useEffect(() => {
    if (echoConnected) {
    } else {
    }
  }, [echoConnected, user]);

  const iconMap = {
    Shield,
    Users,
    User,
    FileText,
    DollarSign,
    Activity,
    FolderOpen,
    Settings,
    UserCheck,
    Key,
    CreditCard,
    Package,
    Tag,
    Files,
    Send,
    Mail,
    Database,
    Bell
  };

  // Get user role information first
  const userRole = getRoleFromUser(user);


  const navigationItems = getNavigationItems(user);

  const allNavigationItems = [...navigationItems];

  const menuItems = allNavigationItems.map(item => ({
    ...item,
    icon: iconMap[item.icon] || Shield
  }));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
      if (fileTypeDropdownRef.current && !fileTypeDropdownRef.current.contains(event.target)) {
        setShowFileTypeDropdown(false);
      }
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounced search for suggestions
  useEffect(() => {
    // Only fetch suggestions if in file manager view and search query is not empty
    if (!location.pathname.includes('/files') || !searchQuery || searchQuery.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        // Extract dataroom UUID from path
        const pathParts = location.pathname.split('/');
        const dataroomIndex = pathParts.indexOf('datarooms');
        if (dataroomIndex === -1 || dataroomIndex + 1 >= pathParts.length) {
          return;
        }
        const dataroomUuid = pathParts[dataroomIndex + 1];

        setSearchLoading(true);
        const response = await fileService.searchFiles(dataroomUuid, searchQuery);

        // Get files from response - try multiple possible structures
        let files = [];
        if (response?.data?.data && Array.isArray(response.data.data)) {
          files = response.data.data;
        } else if (response?.data && Array.isArray(response.data)) {
          files = response.data;
        } else if (Array.isArray(response)) {
          files = response;
        } else if (response?.files && Array.isArray(response.files)) {
          files = response.files;
        }

        // Take only first 5 results for suggestions
        const suggestions = files.slice(0, 5);


        setSearchSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);

      } catch (error) {

        setSearchSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery, location.pathname]);
  
  // Request browser notification permission on mount
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if ('Notification' in window && Notification.permission === 'default') {
        // Check if we should ask for permission
        const lastAsked = localStorage.getItem('notification_permission_timestamp');
        const daysSinceAsked = lastAsked ? (Date.now() - parseInt(lastAsked)) / (1000 * 60 * 60 * 24) : null;
        
        // Only ask if not asked recently (within 7 days)
        if (!lastAsked || daysSinceAsked > 7) {
          try {
            const permission = await Notification.requestPermission();
            localStorage.setItem('notification_permission', permission);
            localStorage.setItem('notification_permission_timestamp', Date.now().toString());
            
            if (permission === 'granted') {
              toast.success('Browser notifications enabled!');
            }
          } catch (error) {
          }
        }
      }
    };
    
    // Request permission after a short delay to not overwhelm the user
    const timer = setTimeout(requestNotificationPermission, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-hide sidebar for dataroom routes
  useEffect(() => {
    const isDataroomFilesRoute = location.pathname.includes('/datarooms/') && location.pathname.includes('/files');
    if (isDataroomFilesRoute) {
      setSidebarOpen(false);
    } else if (!isMobile) {
      setSidebarOpen(true);
    }
  }, [location.pathname, isMobile]);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await notificationApiService.getLatestNotifications();
      setNotifications(response.data || []);
      // const unread = (response.data || []).filter(n => !n.read_at).length;
      // setUnreadCount(unread);
    } catch (error) {
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (notificationDropdownOpen && notifications.length === 0) {
      fetchNotifications();
    }
  }, [notificationDropdownOpen]);

  // Listen for Echo notification events (dispatched by useEchoNotifications hook)
  // Only update the notification list - the hook already handles browser notifications and toasts
  useEffect(() => {
    const handleEchoNotification = async (event) => {
      // Create a new notification object from the Echo event
      const newNotification = {
        id: Date.now(), // Temporary ID, should be replaced with actual ID from backend
        title: event.detail.title || 'New Notification',
        body: event.detail.message || event.detail.body || '',
        data: event.detail,
        type: event.detail.type || 'info',
        read_at: null,
        created_at: new Date().toISOString(),
        action_url: event.detail.url || event.detail.action_url || null
      };

      // Add the new notification to the top of the list
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => {
        const newCount = prev + 1;
        // Update localStorage for persistence
        localStorage.setItem('unreadNotificationCount', newCount.toString());
        return newCount;
      });

      // Optionally refresh notifications from server to get the actual ID
      fetchNotifications();
    };

    // Listen to the custom event dispatched by useEchoNotifications hook
    window.addEventListener('echo:notification', handleEchoNotification);

    return () => {
      window.removeEventListener('echo:notification', handleEchoNotification);
    };
  }, []);

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationApiService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => {
        const newCount = Math.max(0, prev - 1);
        // Update localStorage for persistence
        localStorage.setItem('unreadNotificationCount', newCount.toString());
        return newCount;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    const time = moment(timestamp);
    const now = moment();
    const diffMinutes = now.diff(time, 'minutes');
    
    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else if (diffMinutes < 1440) { // Less than 24 hours
      const hours = Math.floor(diffMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes < 10080) { // Less than a week
      return time.format('ddd, h:mm A');
    } else {
      return time.format('MMM D, YYYY');
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.email || 'Admin';
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'A';
  };

  // Initial load of unread count and listen for localStorage changes
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        // Check localStorage first for immediate display
        const cachedCount = localStorage.getItem('unreadNotificationCount');
        if (cachedCount !== null && cachedCount !== undefined) {
          const parsedCount = parseInt(cachedCount, 10);
          setUnreadCount(parsedCount);
        }

        // Then fetch fresh count from API
        const count = await notificationApiService.getUnreadCount();
        setUnreadCount(count);

        // Update localStorage
        localStorage.setItem('unreadNotificationCount', count.toString());
      } catch (error) {
        console.error('Error loading unread notification count:', error);
      }
    };

    // Listen for storage events (when localStorage changes from other tabs or components)
    const handleStorageChange = (e) => {
      if (e.key === 'unreadNotificationCount' && e.newValue !== null) {
        const newCount = parseInt(e.newValue, 10);
        setUnreadCount(newCount);
      }
    };

    // Also listen for custom events within the same tab
    const handleCustomStorageChange = (e) => {
      const count = localStorage.getItem('unreadNotificationCount');
      if (count !== null) {
        const newCount = parseInt(count, 10);
        setUnreadCount(newCount);
      }
    };

    if (user) {
      loadUnreadCount();

      // Add event listeners
      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('unreadCountUpdated', handleCustomStorageChange);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('unreadCountUpdated', handleCustomStorageChange);
    };
  }, [user]);

  return (
    // <SecurityWrapper enableSecurity={true}> // Security features disabled
      <div className="flex h-screen bg-white">
        {/* Mobile Sidebar Overlay */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

      {/* Sidebar - Google Drive Style */}
      <motion.aside
          initial={false}
          animate={{
            x: sidebarOpen ? 0 : -256,
            width: sidebarOpen ? 256 : 0
          }}
        transition={{ type: "tween", duration: 0.2 }}
        className={`${isMobile ? 'fixed' : 'relative'} h-full z-50 ${sidebarOpen ? 'w-64' : 'w-0'} overflow-hidden`}
        style={{
          backgroundColor: '#FFFFFF',
          borderRight: sidebarOpen ? '1px solid #f0f0f0' : 'none'
        }}
      >
        <div className="h-full flex flex-col w-64">
          <div className="px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #3872FA 0%, #5690FF 100%)' }}
              >
                <Shield className="w-5 h-5 text-white" />
              </div>
                <h1 className="text-[16px] font-bold" style={{ color: '#3872FA' }}>SecureRoom</h1>
              </div>
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100 transition-colors lg:hidden"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>
          </div>

          <nav className="flex-1 py-2 overflow-y-auto">
            <div className="space-y-0.5 relative">
              {menuItems.map((item) => {
                // Check if current path matches or starts with the menu item path (for sub-routes)
                const isActive = item.path === '/dataroom-management'
                  ? location.pathname === item.path || location.pathname.startsWith('/dataroom-management/')
                  : item.path === '/datarooms'
                  ? location.pathname === item.path || location.pathname.startsWith('/datarooms/')
                  : location.pathname === item.path;

                return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center gap-2.5 py-2 transition-all duration-200 group whitespace-nowrap`}
                  style={{
                    backgroundColor: 'transparent',
                    color: isActive ? '#3872FA' : '#000000',
                    paddingLeft: '17px',
                    paddingRight: '12px',
                    marginLeft: '0',
                    marginRight: '0',
                    borderRadius: '0'
                  }}
                  onClick={() => {
                    if (isMobile) {
                      setSidebarOpen(false);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = '#3872FA';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                >
                  {isActive && (
                    <div
                      className="absolute top-1/2 transform -translate-y-1/2"
                      style={{
                        left: '0',
                        width: '3px',
                        height: '25px',
                        backgroundColor: '#3872FA',
                        borderRadius: '0 2px 2px 0'
                      }}
                    />
                  )}
                  <item.icon
                    className="w-4 h-4 flex-shrink-0"
                    style={{
                      color: isActive ? '#3872FA' : 'currentColor'
                    }}
                  />
                  <span className={`text-[14px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis ${
                    isActive ? 'font-semibold' : 'font-normal'
                  }`}>{item.label}</span>
                </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </motion.aside>


      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-200" style={{ backgroundColor: '#FFFFFF' }}>
        {/* Impersonation Banner */}
        {isImpersonating && originalUser && (
          <div className="bg-yellow-500 text-white px-3 py-1.5 text-sm text-center">
            <div className="flex items-center justify-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                You are currently logged in as {user?.name || user?.email}
              </span>
              <span className="text-xs opacity-90">
                (Originally: {originalUser?.name || originalUser?.email})
              </span>
            </div>
          </div>
        )}

        {/* Overlay for search dropdown */}
        {(showSuggestions || searchLoading) && location.pathname.includes('/files') && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity"
            onClick={() => {
              setShowSuggestions(false);
            }}
          />
        )}

        {/* Header */}
        <header className="relative z-50" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #f5f5f5' }}>
          <div className={`flex items-center justify-center ${isMobile ? 'px-3' : 'px-4 md:px-6'} h-14`}>
            {/* Conditional layout based on file manager view */}
            {location.pathname.includes('/files') ? (
              // File Manager view - centered search with dropdown
              <div className="flex items-center justify-center flex-1">
                <div className={`flex items-center gap-3 ${isMobile ? 'w-full max-w-full px-2' : 'w-auto'} ${isMobile ? 'flex' : 'md:flex hidden'}`}>
                  <div className={`relative ${isMobile ? 'flex-1' : 'w-[600px]'}`} ref={searchDropdownRef}>
                    <Search className="absolute left-4 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 md:w-5 h-4 md:h-5" />
                    <input
                      type="text"
                      placeholder={isMobile ? "Search..." : "Search Files and Folders"}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                      className={`w-full ${isMobile ? 'pl-9 pr-3 py-1.5 text-xs' : 'pl-12 pr-4 py-2.5 text-sm'} bg-gray-100 border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all placeholder-gray-500 shadow-sm`}
                    />

                    {/* Search Suggestions Dropdown */}
                    {(showSuggestions || searchLoading) && (
                      <div className={`absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-96 overflow-y-auto ${isMobile ? 'left-0 right-0' : 'left-0 w-[700px]'}`}>
                        {searchLoading && (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></span>
                            <span className="ml-2">Searching...</span>
                          </div>
                        )}
                        {!searchLoading && searchSuggestions.length === 0 && searchQuery.length >= 2 && (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            No files found for "{searchQuery}"
                          </div>
                        )}
                        {!searchLoading && searchSuggestions.length > 0 && searchSuggestions.map((file, index) => (
                          <button
                            key={file.uuid || file.id || index}
                            onClick={() => {

                              // Clear search state immediately before navigation
                              setShowSuggestions(false);
                              setSearchQuery('');
                              setSearchSuggestions([]);

                              // Extract dataroom UUID from current path
                              const pathParts = location.pathname.split('/');
                              const dataroomIndex = pathParts.indexOf('datarooms');
                              if (dataroomIndex !== -1 && dataroomIndex + 1 < pathParts.length) {
                                const dataroomUuid = pathParts[dataroomIndex + 1];

                                // Store file info for highlighting after navigation
                                window.__selectedFileFromSearch = file.uuid || file.id;

                                // Navigate to the folder containing the file
                                if (file.parent_folder_uuid) {
                                  // Build complete breadcrumb path by traversing parent_folder structure
                                  const breadcrumbPath = [];
                                  let currentFolder = file.parent_folder;

                                  // Traverse up the folder hierarchy to build the complete path
                                  while (currentFolder) {
                                    breadcrumbPath.unshift({
                                      uuid: currentFolder.uuid,
                                      id: currentFolder.uuid,
                                      name: currentFolder.name,
                                      display_name: currentFolder.display_name || currentFolder.name
                                    });
                                    currentFolder = currentFolder.parent_folder;
                                  }

                                  // Small delay to ensure state updates before navigation
                                  setTimeout(() => {
                                    window.location.href = `/datarooms/${dataroomUuid}/files?folder=${file.parent_folder_uuid}&path=${encodeURIComponent(JSON.stringify(breadcrumbPath))}`;
                                  }, 50);
                                } else {
                                  // Navigate to root folder
                                  setTimeout(() => {
                                    window.location.href = `/datarooms/${dataroomUuid}/files`;
                                  }, 50);
                                }
                              }
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {file.index_number && (
                                    <span className="text-gray-500 font-normal mr-1">
                                      {file.index_number}.
                                    </span>
                                  )}
                                  {file.name || file.display_name}
                                </div>
                                {file.parent_folder && file.parent_folder.name && (
                                  <div className="flex items-center gap-1 text-xs text-blue-600 truncate mt-1">
                                    <FolderOpen className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{file.parent_folder.name}</span>
                                  </div>
                                )}
                              </div>
                              {file.size && (
                                <div className="text-xs text-gray-400">
                                  {(file.size / 1024).toFixed(1)} KB
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    className={`${isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'} bg-gray-100 border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all cursor-pointer hover:bg-gray-200`}
                  >
                    <option value="filename">File Name Only</option>
                    <option value="file_content">File & Content</option>
                    <option value="content_only">Content Only</option>
                  </select>

                  {/* File Type Filter Dropdown - Show only when "File Name Only" is selected */}
                  {searchType === 'filename' && (
                    <div className="relative" ref={fileTypeDropdownRef}>
                      <button
                        onClick={() => setShowFileTypeDropdown(!showFileTypeDropdown)}
                        className={`${isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'} bg-gray-100 border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-200 transition-all flex items-center gap-1`}
                      >
                        <FileText className="w-4 h-4" />
                        {fileTypes.length > 0 ? `${fileTypes.length} Types` : 'All Types'}
                        <ChevronDown className="w-3 h-3" />
                      </button>

                      {showFileTypeDropdown && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-2">
                          {['pdf', 'image', 'document', 'spreadsheet', 'presentation', 'video', 'audio', 'archive'].map((type) => (
                            <label
                              key={type}
                              className="flex items-center px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={fileTypes.includes(type)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFileTypes([...fileTypes, type]);
                                  } else {
                                    setFileTypes(fileTypes.filter(t => t !== type));
                                  }
                                }}
                                className="mr-2 rounded text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm capitalize">{type}</span>
                            </label>
                          ))}
                          {fileTypes.length > 0 && (
                            <button
                              onClick={() => setFileTypes([])}
                              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-gray-50 border-t border-gray-200 mt-2"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Regular view - normal layout
              <div className="flex items-center space-x-2 md:space-x-4 flex-1">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`${isMobile ? 'p-2' : 'p-2.5'} rounded-full text-gray-600 hover:bg-gray-100 transition-colors`}
                  aria-label="Toggle sidebar"
                  title="Toggle Sidebar"
                >
                  <Menu className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5'}`} />
                </button>

                <div className="relative flex-1 max-w-2xl hidden md:block">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search"
                    className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg pl-10 pr-4 bg-gray-100 text-sm transition-all placeholder-gray-500"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2 md:space-x-4">
              {isImpersonating && (
                <button
                  onClick={async () => {
                    try {
                      await stopImpersonation();
                      navigate('/dashboard');
                    } catch (error) {
                    }
                  }}
                  className="flex items-center gap-2 px-3 md:px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-xl hover:bg-yellow-100 transition-colors font-medium text-xs md:text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden md:inline">Back to Super Admin</span>
                </button>
              )}
              
              {/* Notifications Dropdown */}
              <div className={`${isMobile ? 'static' : 'relative'}`} ref={notificationRef}>
                <button
                  onClick={() => {
                    setNotificationDropdownOpen(!notificationDropdownOpen);
                  }}
                  className={`relative ${isMobile ? 'p-2' : 'p-2.5'} text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors`}
                >
                  <Bell className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                  {unreadCount > 0 && (
                    <span
                      className={`notification-badge absolute -top-1 -right-1 ${isMobile ? 'w-4 h-4' : 'w-5 h-5'} rounded-full flex items-center justify-center font-semibold`}
                      style={{
                        fontSize: isMobile ? '10px' : '12px',
                        lineHeight: '1'
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown Menu */}
                {notificationDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`${isMobile ? 'fixed inset-x-0 mx-4' : 'absolute right-0'} mt-2 ${isMobile ? 'w-auto' : 'w-96'} bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden`}
                  >
                    <div className={`${isMobile ? 'p-4' : 'p-5'} border-b border-gray-100`}>
                      <div className="flex items-center justify-between">
                        <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>Notifications</h3>
                        <button 
                          onClick={async () => {
                            try {
                              await notificationApiService.markAllAsRead();
                              setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
                              setUnreadCount(0);
                              // Update localStorage for persistence
                              localStorage.setItem('unreadNotificationCount', '0');
                              toast.success('All notifications marked as read');
                            } catch (error) {
                              console.error('Error marking all notifications as read:', error);
                            }
                          }}
                          className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-600 hover:text-blue-700 font-medium`}
                        >
                          Mark all as read
                        </button>
                      </div>
                    </div>
                    
                    <div className={`${isMobile ? 'max-h-64' : 'max-h-96'} overflow-y-auto`}>
                      {loadingNotifications ? (
                        <div className="animate-pulse">
                          {[...Array(3)].map((_, index) => (
                            <div key={index} className={`${isMobile ? 'p-3' : 'p-4'} border-b border-gray-200`}>
                              <div className={`flex items-start ${isMobile ? 'gap-2' : 'gap-3'}`}>
                                <div className={`${isMobile ? 'w-2 h-2 mt-1.5' : 'w-2 h-2 mt-1.5'} rounded-full bg-gray-200 flex-shrink-0`} />
                                <div className="flex-1 min-w-0 space-y-2">
                                  <div className={`${isMobile ? 'h-3' : 'h-3.5'} bg-gray-200 rounded w-3/4`}></div>
                                  <div className={`${isMobile ? 'h-2.5' : 'h-3'} bg-gray-200 rounded w-full`}></div>
                                  <div className={`${isMobile ? 'h-2.5' : 'h-3'} bg-gray-200 rounded w-5/6`}></div>
                                  <div className={`${isMobile ? 'h-2' : 'h-2.5'} bg-gray-200 rounded w-20 mt-2`}></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => {
                              if (!notification.read_at) {
                                handleMarkAsRead(notification.id);
                              }
                              const actionUrl = notification.data?.actionUrl || notification.actionUrl || notification.data?.action_url;
                              if (actionUrl) {
                                navigate(actionUrl);
                                setNotificationDropdownOpen(false);
                              }
                            }}
                            className={`${isMobile ? 'p-3' : 'p-4'} border-b border-gray-200 cursor-pointer transition-all ${
                              !notification.read_at
                                ? 'bg-blue-50 hover:bg-blue-100'
                                : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className={`flex items-start ${isMobile ? 'gap-2' : 'gap-3'}`}>
                              {/* Status dot on the left */}
                              <div className={`${isMobile ? 'w-2 h-2 mt-1.5' : 'w-2 h-2 mt-1.5'} rounded-full flex-shrink-0 ${
                                !notification.read_at ? 'bg-blue-500' : 'bg-gray-300'
                              }`} />

                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="flex flex-col gap-1.5">
                                  <p className={`${isMobile ? 'text-xs' : 'text-sm'} ${
                                    !notification.read_at ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                                  } break-words line-clamp-2`}>
                                    {notification.data?.title || notification.title || 'Notification'}
                                  </p>
                                  <p className={`${isMobile ? 'text-[11px]' : 'text-xs'} ${
                                    !notification.read_at ? 'text-gray-700' : 'text-gray-600'
                                  } break-words line-clamp-3 leading-relaxed`}>
                                    {notification.data?.body || notification.body || notification.data?.message || ''}
                                  </p>
                                  <div className={`flex items-center gap-1.5 mt-1`}>
                                    <Clock className={`${isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-gray-400 flex-shrink-0`} />
                                    <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-400 leading-none whitespace-nowrap`}>
                                      {notification.time_ago || formatNotificationTime(notification.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={`${isMobile ? 'p-6' : 'p-8'} text-center`}>
                          <Bell className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} text-gray-300 mx-auto mb-3`} />
                          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>No notifications</p>
                        </div>
                      )}
                    </div>
                    
                    <div className={`${isMobile ? 'p-2' : 'p-3'} border-t border-gray-100`}>
                      <Link 
                        to="/notifications"
                        onClick={() => {
                          setNotificationDropdownOpen(false);
                        }}
                        className={`block w-full text-center ${isMobile ? 'text-xs' : 'text-sm'} text-blue-600 hover:text-blue-700 font-medium ${isMobile ? 'py-1.5' : 'py-2'} hover:bg-gray-50 rounded-lg transition-colors`}
                      >
                        View all notifications
                      </Link>
                    </div>
                  </motion.div>
                )}
              </div>
              
              {/* Profile Dropdown */}
              <div className={`${isMobile ? 'static' : 'relative'}`} ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="h-9 w-9 bg-blue-600 rounded-xl flex items-center justify-center text-white font-semibold shadow-lg shadow-blue-500/20 hover:shadow-xl hover:scale-105 transition-all"
                >
                  {user?.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt={getUserDisplayName()} 
                      className="h-9 w-9 rounded-xl object-cover"
                    />
                  ) : (
                    getUserInitials()
                  )}
                </button>
                
                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`${isMobile ? 'fixed inset-x-0 mx-4' : 'absolute right-0'} mt-2 ${isMobile ? '' : 'w-72'} bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden`}
                  >
                    <div className={`${isMobile ? 'p-4' : 'p-5'} border-b border-gray-100`}>
                      <div className="flex items-center space-x-3">
                        <div className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} bg-blue-600 rounded-xl flex items-center justify-center text-white font-semibold shadow-lg shadow-blue-500/20`}>
                          {user?.profile_picture ? (
                            <img 
                              src={user.profile_picture} 
                              alt={getUserDisplayName()} 
                              className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} rounded-xl object-cover`}
                            />
                          ) : (
                            getUserInitials()
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`${isMobile ? 'text-sm' : 'text-sm'} font-semibold text-gray-900 truncate`}>{getUserDisplayName()}</p>
                          <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 truncate`}>{user?.email}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="py-2">
                      <Link
                        to="/profile"
                        onClick={() => setProfileDropdownOpen(false)}
                        className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
                      >
                        <User className="w-4 h-4" />
                        <span>My Profile</span>
                      </Link>

                      <hr className="my-2 border-gray-200" />
                      
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          logout();
                          // Navigation handled by ProtectedRoute when auth state changes
                        }}
                        className="flex items-center space-x-3 px-3 py-1.5.5 mx-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left rounded-lg"
                      >
                        <div className="p-1.5 bg-red-50 rounded-lg">
                          <LogOut className="w-4 h-4 text-red-600" />
                        </div>
                        <span className="font-medium">Logout</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className={`flex-1 ${location.pathname.includes('/datarooms/') && location.pathname.includes('/files') ? 'overflow-hidden' : 'overflow-y-auto'}`} style={{ backgroundColor: '#FFFFFF' }}>
          {/* Remove padding for dataroom file manager to prevent scrolling issues */}
          {location.pathname.includes('/datarooms/') && location.pathname.includes('/files') ? (
            <div className="h-full">
              {children}
            </div>
          ) : (
            <div className="p-4 md:p-6 lg:p-8 mx-auto">
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
    // </SecurityWrapper> // Security features disabled
  );
};

export default Layout;